import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../api';
import NoteCard from './NoteCard';
import SummaryView from './SummaryView';

/**
 * BrowseNotesTab - Tab for browsing all Google meeting notes from Drive
 */
const BrowseNotesTab = ({
  googleEmail,
  formatDate
}) => {
  // Notes list state
  const [notes, setNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteContent, setNoteContent] = useState(null);

  // Filter state
  const [daysBack, setDaysBack] = useState(30);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);

  // Error state
  const [error, setError] = useState(null);

  // Fetch all notes from Drive
  const fetchNotes = useCallback(async () => {
    if (!googleEmail) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(
        `/api/google/meet/browse-notes?email=${encodeURIComponent(googleEmail)}&days_back=${daysBack}`
      );
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        setNotes([]);
      } else {
        setNotes(data.notes || []);
      }
    } catch (err) {
      console.error('Error browsing notes:', err);
      setError('Failed to browse meeting notes');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [googleEmail, daysBack]);

  // Fetch notes on mount and when days filter changes
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Handle note selection
  const handleSelectNote = async (note) => {
    if (selectedNote?.doc_id === note.doc_id) return;

    setSelectedNote(note);
    setNoteContent(null);
    setLoadingContent(true);

    try {
      const response = await apiFetch(
        `/api/google/meet/notes/${note.doc_id}/content?email=${encodeURIComponent(googleEmail)}`
      );
      const data = await response.json();

      if (data.error) {
        setNoteContent({ error: data.error });
      } else {
        setNoteContent(data);
      }
    } catch (err) {
      console.error('Error fetching note content:', err);
      setNoteContent({ error: 'Failed to load note content' });
    } finally {
      setLoadingContent(false);
    }
  };

  // Handle refresh
  const handleRefreshContent = async () => {
    if (!selectedNote) return;
    await handleSelectNote(selectedNote);
  };

  return (
    <div className="browse-notes-tab">
      {/* Filter bar */}
      <div className="browse-notes-filter">
        <div className="filter-group">
          <label htmlFor="days-filter">Show notes from last:</label>
          <select
            id="days-filter"
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="days-select"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>

        <button
          className="btn btn-secondary btn-small"
          onClick={fetchNotes}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="browse-notes-error">
          <p>{error}</p>
        </div>
      )}

      {/* Master-Detail Layout */}
      <div className="master-detail-layout browse-notes-layout">
        {/* Left Panel: Notes List */}
        <div className="transcript-list-panel">
          {loading && notes.length === 0 ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Searching Google Drive...</p>
            </div>
          ) : notes.length === 0 ? (
            <div className="empty-list-state">
              <div className="empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
              </div>
              <p>No meeting notes found</p>
              <p className="hint">
                Meeting notes are created when you enable "Take notes for me" in Google Meet.
                Try expanding the date range or starting a new meeting with notes enabled.
              </p>
            </div>
          ) : (
            <div className="transcript-list">
              <div className="notes-count">
                {notes.length} note{notes.length !== 1 ? 's' : ''} found
              </div>
              {notes.map((note) => (
                <NoteCard
                  key={note.doc_id}
                  note={note}
                  isSelected={selectedNote?.doc_id === note.doc_id}
                  onClick={handleSelectNote}
                  formatDate={formatDate}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right Panel: Note Content */}
        <div className="transcript-detail-wrapper">
          {!selectedNote ? (
            <div className="detail-panel-empty">
              <div className="empty-state-content">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                </svg>
                <h3>Select a Note</h3>
                <p>Choose a meeting note from the list to view its content</p>
              </div>
            </div>
          ) : (
            <div className="note-detail-panel">
              {/* Note Header */}
              <div className="note-detail-header">
                <h2>{selectedNote.title || 'Meeting Notes'}</h2>
                <div className="note-detail-meta">
                  <span className="note-date">
                    {formatDate ? formatDate(selectedNote.modified_time) : new Date(selectedNote.modified_time).toLocaleString()}
                  </span>
                  {selectedNote.meeting_code && (
                    <>
                      <span className="meta-separator">•</span>
                      <span className="meeting-code">{selectedNote.meeting_code}</span>
                    </>
                  )}
                  <a
                    href={selectedNote.doc_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="open-docs-link"
                  >
                    Open in Docs
                  </a>
                </div>
              </div>

              {/* Note Content using SummaryView */}
              <div className="note-content-wrapper">
                <SummaryView
                  summary={noteContent?.summary}
                  summarySource={noteContent?.source || 'GOOGLE'}
                  summaryGeneratedAt={null}
                  googleNotesDocId={selectedNote.doc_id}
                  loading={loadingContent}
                  error={noteContent?.error}
                  onRegenerate={handleRefreshContent}
                  students={[]}
                  linkedStudentUuid={null}
                  onStudentLink={() => {}}
                  linkingStudent={false}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowseNotesTab;
